/**
 * Delta Report — Spec Bölüm 8.4 + S5.6
 *
 *   /yonetici/sinavlar/:id/delta
 *
 * Followup sınavın önceki sınava göre delta'sını gösterir.
 * - Ekip ortalama farkı
 * - Aday bazlı tablo (ilerleme/gerileme)
 * - Konu bazlı delta (renkli)
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface DeltaRow {
  displayName: string;
  previousScore: number;
  currentScore: number;
  delta: number;
  topicDeltas: Record<string, number>;
}

interface DeltaResponse {
  previous: {
    id: string;
    accessCode: string;
    createdAt: string;
    teamAverage: number;
    attemptCount: number;
  };
  current: {
    id: string;
    accessCode: string;
    createdAt: string;
    teamAverage: number;
    attemptCount: number;
  };
  teamDelta: number;
  matchedCandidates: number;
  rows: DeltaRow[];
}

const TOPIC_LABELS: Record<string, string> = {
  PM: 'PM',
  AS: 'AS',
  DR: 'DR',
  SK: 'SK',
  SM: 'SM',
  WD: 'WD',
  SF: 'SF',
  CF: 'CF',
  GN: 'GN',
  DT: 'DT',
};

export function ExamDeltaPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const params = useParams<{ id: string }>();
  const examId = params.id ?? '';
  const [data, setData] = useState<DeltaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await api.get<DeltaResponse>(
          `/api/manager/exams/${examId}/delta`,
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Delta yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [examId, session, sessionPending, setLocation]);

  if (sessionPending || (!data && !error)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-5xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-5xl px-6 py-12">
          <Alert variant="danger">{error ?? 'Delta bulunamadı'}</Alert>
          <p className="mt-4 text-sm text-slate-600">
            Delta raporu için sınav, başka bir sınavın "followup"u olarak
            oluşturulmuş olmalıdır. Sınav detayında "Tekrar sınav oluştur"
            seçeneği kullanın.
          </p>
        </main>
      </div>
    );
  }

  const teamArrow = data.teamDelta > 0 ? '↑' : data.teamDelta < 0 ? '↓' : '→';
  const teamColor =
    data.teamDelta > 0
      ? 'text-green-700'
      : data.teamDelta < 0
      ? 'text-red-700'
      : 'text-slate-700';

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <button
          type="button"
          onClick={() => setLocation(`/yonetici/sinavlar/${examId}`)}
          className="text-sm font-medium text-blue-900 hover:underline"
        >
          ← Sınav Detayı
        </button>

        <h1 className="mt-2 text-3xl font-bold text-slate-900">Delta Rapor</h1>
        <p className="mt-1 text-slate-600">
          Spec 8.4 — Bu tekrar sınavın önceki sınava göre ilerlemesi.
        </p>

        {/* Team delta card */}
        <Card className="mt-6">
          <div className="grid items-center gap-4 sm:grid-cols-3">
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-slate-500">
                Önceki Sınav
              </p>
              <Link
                href={`/yonetici/sinavlar/${data.previous.id}`}
                className="block font-mono text-sm text-blue-900 hover:underline"
              >
                {data.previous.accessCode}
              </Link>
              <p className="mt-3 text-4xl font-bold text-slate-900">
                {data.previous.teamAverage}
              </p>
              <p className="text-xs text-slate-500">
                {data.previous.attemptCount} aday ·{' '}
                {new Date(data.previous.createdAt).toLocaleDateString('tr-TR')}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-slate-500">
                Ekip Delta
              </p>
              <p className={`mt-3 text-5xl font-bold ${teamColor}`}>
                {teamArrow}{' '}
                {data.teamDelta > 0 ? `+${data.teamDelta}` : data.teamDelta}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {data.matchedCandidates} aday eşleşti
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-slate-500">
                Bu Sınav
              </p>
              <p className="font-mono text-sm text-slate-700">
                {data.current.accessCode}
              </p>
              <p className="mt-3 text-4xl font-bold text-blue-900">
                {data.current.teamAverage}
              </p>
              <p className="text-xs text-slate-500">
                {data.current.attemptCount} aday ·{' '}
                {new Date(data.current.createdAt).toLocaleDateString('tr-TR')}
              </p>
            </div>
          </div>
        </Card>

        {/* Aday delta tablosu */}
        <Card className="mt-6">
          <h2 className="text-lg font-semibold text-slate-900">
            Aday Bazlı İlerleme
          </h2>
          {data.rows.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">
              Eşleşen aday bulunamadı. Aynı adayların iki sınava da katılması
              gerekir.
            </p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Aday</th>
                    <th className="py-2 pr-3 text-right">Önceki</th>
                    <th className="py-2 pr-3 text-right">Bu</th>
                    <th className="py-2 pr-3 text-right">Delta</th>
                    <th className="py-2 pl-3">Konu Δ (en çok değişen)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.rows.map((r, i) => {
                    const arrow = r.delta > 0 ? '↑' : r.delta < 0 ? '↓' : '→';
                    const color =
                      r.delta > 0
                        ? 'text-green-700'
                        : r.delta < 0
                        ? 'text-red-700'
                        : 'text-slate-700';
                    const topTopicChanges = Object.entries(r.topicDeltas)
                      .filter(([, d]) => d !== 0)
                      .sort(([, a], [, b]) => Math.abs(b) - Math.abs(a))
                      .slice(0, 3);
                    return (
                      <tr key={i}>
                        <td className="py-2 pr-3 font-medium text-slate-900">
                          {r.displayName}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-600">
                          {r.previousScore}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-900">
                          {r.currentScore}
                        </td>
                        <td className={`py-2 pr-3 text-right font-bold ${color}`}>
                          {arrow} {r.delta > 0 ? `+${r.delta}` : r.delta}
                        </td>
                        <td className="py-2 pl-3">
                          {topTopicChanges.length === 0 ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {topTopicChanges.map(([topic, d]) => (
                                <span
                                  key={topic}
                                  className={`rounded-full px-2 py-0.5 text-xs ${
                                    d > 0
                                      ? 'bg-green-50 text-green-800'
                                      : 'bg-red-50 text-red-700'
                                  }`}
                                >
                                  {TOPIC_LABELS[topic] ?? topic}{' '}
                                  {d > 0 ? `+${d}` : d}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
